import { defineStore } from "pinia";
import { getGuideById } from "@/help/registry";
const resolveActiveStep = (guide, stepId, context) => {
    if (!guide || !stepId)
        return null;
    const step = guide.steps.find((item) => item.id === stepId);
    if (!step)
        return null;
    if (step.when && !step.when(context))
        return null;
    return step;
};
export const useInteractiveHelpStore = defineStore("interactiveHelp", {
    state: () => ({
        activeGuideId: null,
        activeStepId: null,
        status: "idle",
        context: {},
    }),
    getters: {
        activeGuide(state) {
            return state.activeGuideId
                ? getGuideById(state.activeGuideId) ?? null
                : null;
        },
        activeStep(state) {
            const guide = state.activeGuideId
                ? getGuideById(state.activeGuideId) ?? null
                : null;
            return resolveActiveStep(guide, state.activeStepId, state.context);
        },
        isActive(state) {
            return state.status === "running" || state.status === "paused";
        },
    },
    actions: {
        startGuide(guideId, guideContext = {}) {
            const guide = getGuideById(guideId);
            if (!guide?.steps.length) {
                this.status = "failed";
                return false;
            }
            const firstStep = guide.steps.find((step) => !step.when || step.when(guideContext)) ??
                guide.steps[0];
            if (!firstStep) {
                this.status = "failed";
                return false;
            }
            this.activeGuideId = guideId;
            this.activeStepId = firstStep.id;
            this.context = { ...guideContext };
            this.status = "running";
            return true;
        },
        goToStep(stepId) {
            const guide = this.activeGuide;
            if (!guide)
                return false;
            const step = guide.steps.find((item) => item.id === stepId);
            if (!step)
                return false;
            if (step.when && !step.when(this.context))
                return false;
            this.activeStepId = stepId;
            this.status = "running";
            return true;
        },
        nextStep() {
            const guide = this.activeGuide;
            if (!guide || !this.activeStepId)
                return false;
            const currentIndex = guide.steps.findIndex((step) => step.id === this.activeStepId);
            if (currentIndex < 0)
                return false;
            const next = guide.steps
                .slice(currentIndex + 1)
                .find((step) => !step.when || step.when(this.context));
            if (!next) {
                this.completeGuide();
                return true;
            }
            this.activeStepId = next.id;
            this.status = "running";
            return true;
        },
        cancelGuide() {
            this.activeGuideId = null;
            this.activeStepId = null;
            this.context = {};
            this.status = "cancelled";
        },
        failGuide() {
            this.activeGuideId = null;
            this.activeStepId = null;
            this.context = {};
            this.status = "failed";
        },
        completeGuide() {
            this.activeGuideId = null;
            this.activeStepId = null;
            this.context = {};
            this.status = "completed";
        },
        reset() {
            this.activeGuideId = null;
            this.activeStepId = null;
            this.context = {};
            this.status = "idle";
        },
        setContext(nextContext) {
            this.context = { ...this.context, ...nextContext };
        },
        handleAdvanceEvent(eventName) {
            if (!this.isActive || !this.activeStep)
                return false;
            const expectedEvent = this.activeStep.advanceOn?.event;
            if (!expectedEvent || expectedEvent !== eventName)
                return false;
            return this.nextStep();
        },
    },
});
